import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fetchRegions } from '../lib/metaApi';

const STORAGE_KEY = 'ucg.metaRegion';

/** localStorage is unavailable in private mode and some embedded webviews. */
function readStoredRegion() {
  try {
    return window.localStorage.getItem(STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

function storeRegion(region) {
  try {
    window.localStorage.setItem(STORAGE_KEY, region);
  } catch {
    /* a preference we cannot persist is not worth failing a render over */
  }
}

/**
 * The currently selected tournament region, and the regions available to pick.
 *
 * Resolution order is deliberate:
 *   1. `?region=` in the URL — so a shared link always shows what the sharer saw.
 *   2. The visitor's last choice, remembered across sessions.
 *   3. The server's default (the largest corpus, currently Japan).
 *
 * A stored or linked region that no longer has data falls back to the default
 * rather than rendering an empty page.
 *
 * `region` is undefined until the region list resolves. Callers should hold off
 * fetching until then, otherwise the first request goes out against the server
 * default and a second fires immediately after — a visible double-load and a
 * flash of the wrong region's numbers.
 */
export function useRegion() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [index, setIndex] = useState(null);
  const [loading, setLoading] = useState(true);

  const urlRegion = searchParams.get('region');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchRegions();
        if (!cancelled) setIndex(data);
      } catch {
        // The switcher is a convenience; if the list cannot load, the pages still
        // work against the server's default region.
        if (!cancelled) setIndex({ regions: [], defaultRegion: null });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const regions = useMemo(() => index?.regions || [], [index]);

  const region = useMemo(() => {
    if (!index) return undefined;                   // not resolved yet
    const known = (name) => name && regions.some((r) => r.region === name);
    if (known(urlRegion)) return urlRegion;
    const stored = readStoredRegion();
    if (known(stored)) return stored;
    return index.defaultRegion || regions[0]?.region || null;
  }, [index, regions, urlRegion]);

  // Remember the visitor's region, but only once it is a real choice — writing the
  // resolved default back would pin whatever they happened to land on first.
  useEffect(() => {
    if (region && urlRegion === region) storeRegion(region);
  }, [region, urlRegion]);

  const setRegion = useCallback((next) => {
    storeRegion(next);
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set('region', next);
      // Region changes the whole result set, so any page cursor is meaningless.
      params.delete('page');
      return params;
    });
  }, [setSearchParams]);

  const current = useMemo(
    () => regions.find((r) => r.region === region) || null,
    [regions, region]
  );

  return {
    region,
    setRegion,
    regions,
    /** The index entry for the active region (coverage, minEntries, subregion flags). */
    currentRegion: current,
    loading,
    /** Only worth showing a switcher when there is something to switch to. */
    hasChoice: regions.length > 1,
  };
}

export default useRegion;
